import type {
  Currency,
  ExtraFreightUpdatedSubscription,
  ExtraFreightUpdatedSubscriptionVariables,
  PlanningPageQuery,
  VoyageIncomeQuery,
  VoyageIncomeQuery_VoyageCharterFragment,
} from '../../graphql';
import {
  VoyageIncomeDocument,
  useExtraFreightUpdatedSubscription,
  useAllWorldscaleFreightsQuery,
} from '../../graphql';

import { useCurrentUser, useDefaultCurrency } from '../../state/zustand';
import { usePortCosts } from './usePortCost';
import type { PortCostEstimateMaps } from './usePortCostEstimates';
import { usePortCostsCoa } from './usePortCostCoa';
import { useMemo } from 'react';
import type { SubscriptionHookOptions } from '@apollo/client';
import { cloneDeep, compact } from 'lodash';
import type { CoaPrice, CoaPriceIndex } from '../contracts/coa_prices';
import { buildCoaPriceIndex } from '../contracts/coa_prices';
import { mapKeyBy, withDefined } from '../../lib/util';
import {
  buildWorldscaleFreightRateMap,
  type WorldscaleFreightRateMap,
} from '../../lib/worldscaleFreight';
import { DefaultMap } from '@seaber/common/lib/collections';
import type { ScenarioOrder } from '../../lib/planningData';

export type IndexedCoaPricesByCoaId = Map<number, CoaPriceIndex>;

export type PnlData = {
  incomingVoyageChartersByOrderId: Map<
    number,
    VoyageIncomeQuery_VoyageCharterFragment
  >;
  incomingVoyageChartersById: Map<
    number,
    VoyageIncomeQuery_VoyageCharterFragment
  >;
  indexedCoaPricesByCoaId: IndexedCoaPricesByCoaId;
  outgoingVoyageChartersById: Map<
    number,
    VoyageIncomeQuery_VoyageCharterFragment
  >;
  portCostByVcMaps: ReturnType<typeof usePortCosts>;
  portCostEstimateMaps: PortCostEstimateMaps;
  portCostByCoaMaps: ReturnType<typeof usePortCostsCoa>;
  worldscaleFreightRates: WorldscaleFreightRateMap;
};

export function usePnlData(
  orderById: Map<number, ScenarioOrder>,
  data: PlanningPageQuery | undefined,
  portCostEstimateMaps: PortCostEstimateMaps,
): PnlData {
  const currentUser = useCurrentUser();
  const currentUserOrg = currentUser?.organizationId;
  const shipownerOrgId = currentUser?.organizationId ?? null;
  const defaultCurrency = useDefaultCurrency();

  const { data: worldscaleData } = useAllWorldscaleFreightsQuery({
    skip: !currentUserOrg,
  });

  const worldscaleFreightRates = useMemo(
    () =>
      buildWorldscaleFreightRateMap(
        worldscaleData?.allWorldscaleFreights?.nodes,
      ),
    [worldscaleData],
  );

  const voyageCharterMaps = useMemo(
    () => getVoyageCharterPnlMaps(orderById, shipownerOrgId),
    [orderById, shipownerOrgId],
  );

  const extraFreightOptions = useMemo(() => {
    const options: SubscriptionHookOptions<
      ExtraFreightUpdatedSubscription,
      ExtraFreightUpdatedSubscriptionVariables
    > = {
      variables: {
        org: `${currentUserOrg}`,
      },
      skip: currentUserOrg == null,
      onData: async (options) => {
        const { data, client } = options;
        const { event, extraFreight } =
          data.data?.voyageCharterExtraFreightUpdated || {};
        const dataInStore = client.readQuery<VoyageIncomeQuery>({
          query: VoyageIncomeDocument,
        });
        if (!dataInStore) return;

        const next = cloneDeep(dataInStore);
        const allVoyageCharters = next.allVoyageCharters?.nodes;
        if (!allVoyageCharters) return;

        if (extraFreight) {
          const voyageCharterIndex = allVoyageCharters.findIndex(
            (node) => node?.id === extraFreight.voyageCharterId,
          );
          if (voyageCharterIndex === -1) return;
          const voyageCharter = allVoyageCharters[voyageCharterIndex];
          const extraFreights =
            voyageCharter?.voyageCharterExtraFreightsByVoyageCharterId.nodes;
          if (!extraFreights) return;
          const extraFreightIndex = extraFreights.findIndex(
            (ef) => ef?.id === extraFreight.id,
          );
          // Insert
          if (extraFreightIndex === -1) {
            extraFreights.push(extraFreight);
            client.writeQuery<VoyageIncomeQuery>({
              query: VoyageIncomeDocument,
              data: {
                ...next,
              },
            });
            return;
          }
          // Update
          else {
            extraFreights[extraFreightIndex] = {
              ...extraFreights[extraFreightIndex],
              ...extraFreight,
            };

            client.writeQuery<VoyageIncomeQuery>({
              query: VoyageIncomeDocument,
              data: {
                ...next,
              },
            });
            return;
          }
        } else if (event) {
          // Delete extra freight
          for (const vc of allVoyageCharters) {
            const extraFreights =
              vc?.voyageCharterExtraFreightsByVoyageCharterId.nodes;
            if (!extraFreights || !extraFreights.length) continue;
            const extraFreightIndex = extraFreights.findIndex(
              (ef) => ef?.id === Number(event),
            );
            if (extraFreightIndex !== -1) {
              extraFreights.splice(extraFreightIndex, 1);
              client.writeQuery<VoyageIncomeQuery>({
                query: VoyageIncomeDocument,
                data: {
                  ...next,
                },
              });
              return;
            }
          }
        }
      },
    };
    return options;
  }, [currentUserOrg]);
  useExtraFreightUpdatedSubscription(extraFreightOptions);

  const indexedCoaPricesByCoaId = useMemo(
    () => getIndexedCoaPricesByCoaId(data, defaultCurrency),
    [defaultCurrency, data],
  );

  const portCostByVcMaps = usePortCosts();
  const portCostByCoaMaps = usePortCostsCoa();

  /** Memoized so that you can pass the whole returned object down the tree */
  const pnlData = useMemo(
    () => ({
      indexedCoaPricesByCoaId,
      portCostByVcMaps,
      portCostEstimateMaps,
      portCostByCoaMaps,
      worldscaleFreightRates,
      ...voyageCharterMaps,
    }),
    [
      indexedCoaPricesByCoaId,
      portCostByVcMaps,
      portCostEstimateMaps,
      portCostByCoaMaps,
      worldscaleFreightRates,
      voyageCharterMaps,
    ],
  );

  return pnlData;
}

export function getIndexedCoaPricesByCoaId(
  data: PlanningPageQuery | undefined,
  defaultCurrency: Currency,
) {
  const assortmentsByAssortmentGroupId = new DefaultMap<number, number[]>(
    () => [],
  );
  for (const a of data?.allAssortments?.nodes ?? []) {
    const groupId = a?.assortmentGroupByAssortmentGroup?.id;
    if (groupId) {
      assortmentsByAssortmentGroupId.get(groupId).push(a.id);
    }
  }

  const locationsByZoneId = new Map<number, number[]>(
    compact(data?.allZones?.nodes).map((zone) => [
      zone.id,
      compact(zone.zoneLocationsByZoneId.nodes).map((node) => node.id),
    ]),
  );

  const map: IndexedCoaPricesByCoaId = new Map();

  for (const coa of data?.allCoaContracts?.nodes ?? []) {
    if (!coa) continue;
    const prices: CoaPrice[] = [];

    const coaAssortmentById = mapKeyBy(
      compact(coa.coaContractAssortmentsByCoaContractId.nodes),
      (ca) => ca.id,
    );

    for (const coaPrice of coa.coaContractPricesByCoaContractId.nodes) {
      if (!coaPrice?.coaContractPortByRouteId) continue;
      const coaAssortment = coaAssortmentById.get(coaPrice.coaAssortmentId);
      const portPair = coaPrice.coaContractPortByRouteId;
      if (!coaAssortment || !portPair) continue;

      prices.push({
        assortmentGroupId: coaAssortment.assortmentGroupId ?? null,
        assortmentId: coaAssortment.assortmentId ?? null,
        cargoUnit: coa.cargoUnitByAmountUnitId?.name ?? null,
        currency: coa.currency ?? defaultCurrency,
        dischargePortId: portPair.destinationId ?? null,
        dischargeZoneId: portPair.destinationZoneId ?? null,
        loadPortId: portPair.originId ?? null,
        loadZoneId: portPair.originZoneId ?? null,
        maxAmount: withDefined(coaAssortment.maxAmount, Number),
        minAmount: withDefined(coaAssortment.minAmount, Number),
        paymentUnit: coaAssortment.paymentUnit,
        price: +coaPrice.price,
      });
    }

    const index = buildCoaPriceIndex(
      prices,
      assortmentsByAssortmentGroupId,
      locationsByZoneId,
    );

    map.set(coa.id, index);
  }

  return map;
}

export function getVoyageCharterPnlMaps(
  orderById: Map<number, ScenarioOrder>,
  shipownerOrgId: number | null,
) {
  const incomingVoyageChartersByOrderId: PnlData['incomingVoyageChartersByOrderId'] =
    new Map();
  const incomingVoyageChartersById: PnlData['incomingVoyageChartersById'] =
    new Map();
  const outgoingVoyageChartersById: PnlData['outgoingVoyageChartersById'] =
    new Map();

  for (const o of orderById.values()) {
    const incomingVc =
      o.incomingVoyageCharterRelation?.voyageCharterByVoyageCharterId;
    if (incomingVc && incomingVc.shipownerOrgId === shipownerOrgId) {
      incomingVoyageChartersByOrderId.set(o.id, incomingVc);
      incomingVoyageChartersById.set(incomingVc.id, incomingVc);
    }
    const outgoingVc =
      o.outgoingVoyageCharterRelation?.voyageCharterByVoyageCharterId;
    if (outgoingVc) {
      outgoingVoyageChartersById.set(outgoingVc.id, outgoingVc);
    }
  }

  return {
    incomingVoyageChartersByOrderId,
    incomingVoyageChartersById,
    outgoingVoyageChartersById,
  };
}
